/**
 * HobbyGundam custom storefront behavior.
 * Keep everything here (or in other hg-* files) so Horizon updates merge cleanly.
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------
   * Imported consent gate (blocks/hg-imported-consent.liquid)
   *
   * Add to cart is already blocked by native validation (the checkbox is
   * `required` and tied to the product form). Buy it now / Shop Pay skip
   * form validation, so we keep those buttons inert until the box is ticked.
   * Horizon toggles `hidden` on the accelerated-checkout container on every
   * variant change, so we use `inert` + a class instead and re-apply after
   * any re-render.
   * ------------------------------------------------------------------ */
  const LOCK_CLASS = 'hg-buy-locked';
  const EXPRESS_SELECTOR = "[ref='acceleratedCheckoutButtonContainer'], .shopify-payment-button";
  const observed = new WeakSet();

  function formFor(consent) {
    return document.getElementById(consent.dataset.formId || '');
  }

  // Lock = disabled until the box is ticked. Remembers whether Horizon had the button
  // disabled itself (e.g. sold out) so unlocking never enables an unavailable variant.
  function lockButton(btn) {
    if (!btn.hasAttribute('data-hg-locked')) {
      btn.setAttribute('data-hg-locked', '');
      btn.dataset.hgWasDisabled = btn.disabled ? '1' : '0';
    } else if (!btn.disabled) {
      // Horizon re-enabled it (variant change): it's available, keep it locked.
      btn.dataset.hgWasDisabled = '0';
    }
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
  }

  function unlockButton(btn) {
    if (!btn.hasAttribute('data-hg-locked')) return;
    btn.disabled = btn.dataset.hgWasDisabled === '1';
    btn.removeAttribute('data-hg-locked');
    btn.removeAttribute('aria-disabled');
    delete btn.dataset.hgWasDisabled;
  }

  function syncConsent(consent) {
    const input = consent.querySelector('[data-hg-consent-input]');
    const form = formFor(consent);
    if (!input || !form) return;
    const productForm = form.closest('product-form-component') || form.parentElement;
    const section = form.closest('.shopify-section') || productForm;

    const locked = !input.checked;
    productForm.classList.toggle(LOCK_CLASS, locked);
    productForm.querySelectorAll(EXPRESS_SELECTOR).forEach((el) => {
      if (locked) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });

    // Main Add to cart button + the sticky bar's button (which "clicks" the main one).
    const buttons = [
      form.querySelector("[ref='addToCartButton']"),
      section.querySelector("sticky-add-to-cart [ref='addToCartButton']"),
    ].filter(Boolean);
    buttons.forEach((btn) => (locked ? lockButton(btn) : unlockButton(btn)));

    if (!observed.has(section)) {
      observed.add(section);
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          syncAllConsents();
        });
      }).observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
    }
  }

  function syncAllConsents() {
    document.querySelectorAll('[data-hg-consent]').forEach(syncConsent);
  }

  document.addEventListener('change', (event) => {
    const consent = event.target instanceof Element ? event.target.closest('[data-hg-consent]') : null;
    if (consent) syncConsent(consent);
  });

  // Product details can be re-rendered (variant change, quick view): watch for new consent blocks.
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      syncAllConsents();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------------
   * Mira-Chan chat launcher
   *
   * A bobbing Mira-Chan avatar above Shopify Inbox's own chat button, with a
   * one-time speech bubble. Clicking opens the same Inbox chat (Horizon hosts
   * <shopify-chat> in snippets/chat-drawer.liquid). If Inbox never loads, the
   * launcher never appears; Shopify's own button keeps working either way.
   * ------------------------------------------------------------------ */
  const BUBBLE_KEY = 'hg-mira-bubble-dismissed';

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function openChat(chat) {
    if (typeof chat.show === 'function') chat.show();
    else chat.setAttribute('open', '');
  }

  function initMira() {
    const cfg = window.HG && window.HG.mira;
    const chat = document.querySelector('shopify-chat');
    if (!cfg || !cfg.avatar || !chat || document.querySelector('.hg-mira')) return;

    const root = document.createElement('div');
    root.className = 'hg-mira';

    const bubble = document.createElement('div');
    bubble.className = 'hg-mira__bubble';
    bubble.hidden = true;
    bubble.innerHTML =
      '<button type="button" class="hg-mira__close" aria-label="Dismiss">×</button>' +
      '<p class="hg-mira__greeting"></p><p class="hg-mira__prompt"></p><div class="hg-mira__chips"></div>' +
      '<p class="hg-mira__notice"><span></span> <a href="/policies/terms-of-service">Terms</a> &middot; <a href="/policies/privacy-policy">Privacy</a></p>';
    bubble.querySelector('.hg-mira__greeting').textContent = cfg.greeting || '';
    bubble.querySelector('.hg-mira__prompt').textContent = cfg.prompt || '';
    bubble.querySelector('.hg-mira__notice span').textContent = cfg.notice || '';
    const chips = bubble.querySelector('.hg-mira__chips');
    (cfg.chips || []).forEach((label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.addEventListener('click', () => { dismissBubble(); openChat(chat); });
      chips.appendChild(b);
    });

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hg-mira__button';
    button.setAttribute('aria-label', 'Chat with Mira-Chan');
    const img = document.createElement('img');
    img.src = cfg.avatar;
    img.alt = '';
    img.width = 64;
    img.height = 64;
    button.appendChild(img);
    button.addEventListener('click', () => { dismissBubble(); openChat(chat); });

    root.append(bubble, button);
    document.body.appendChild(root);

    function dismissBubble() {
      bubble.hidden = true;
      storageSet(BUBBLE_KEY, '1');
    }
    bubble.querySelector('.hg-mira__close').addEventListener('click', dismissBubble);

    // Hide the launcher while the chat is open.
    const syncOpen = () => root.classList.toggle('is-chat-open', chat.hasAttribute('open'));
    new MutationObserver(syncOpen).observe(chat, { attributes: true, attributeFilter: ['open'] });
    syncOpen();

    // While Mira is visible, hide Shopify's own "Ask anything" button so only one launcher shows.
    // Uses the button's public CSS part (shopify-chat::part(activator), see hg-custom.css).
    // Product pages too: Horizon's sticky add-to-cart bar never shows while Inbox chat is installed
    // (sticky-add-to-cart.js #isChatActive), so there's nothing for Mira to step aside for.
    const syncActivator = () => {
      document.documentElement.classList.toggle('hg-mira-on', root.classList.contains('is-ready'));
    };

    // Show only once Inbox has registered the chat element.
    customElements.whenDefined('shopify-chat').then(() => {
      root.classList.add('is-ready');
      syncActivator();
      if (!storageGet(BUBBLE_KEY)) {
        window.setTimeout(() => {
          if (!chat.hasAttribute('open')) bubble.hidden = false;
        }, 6000);
      }
    });
  }

  /* ------------------------------------------------------------------
   * Google "Store quality" widget (Google & YouTube app embed)
   *
   * Google positions its badge inline with !important at the bottom-left,
   * on top of the Smile.io Rewards pill. Stack it just above the Rewards pill
   * (measured, so it follows Smile's real size) and keep it there if Google
   * rewrites the style. Hidden on phones (it covers content on small screens).
   * ------------------------------------------------------------------ */
  const GOOGLE_LEFT = '4px';
  const GOOGLE_GAP = 2;
  const phoneQuery = window.matchMedia('(max-width: 749px)');

  function googleBottom() {
    const smile = document.getElementById('smile-ui-lite-launcher-frame-container');
    const r = smile ? smile.getBoundingClientRect() : null;
    if (r && r.height > 0) return `${Math.round(window.innerHeight - r.top + GOOGLE_GAP)}px`;
    return '76px';
  }

  function labelGoogleFrame(el) {
    // Screen readers need a name for the iframe (WCAG 4.1.2); Google doesn't set one.
    const frame = el.querySelector('iframe') || document.getElementById('merchantwidgetiframe');
    if (frame && !frame.title) frame.title = 'Google store quality rating';
  }

  function placeGoogleWidget(el) {
    labelGoogleFrame(el);
    // Phones: hidden on every page (it covered article text, the 404 and product buttons).
    const hide = phoneQuery.matches;
    if (hide && el.style.getPropertyValue('display') !== 'none') {
      el.style.setProperty('display', 'none', 'important');
      el.dataset.hgHidden = '1';
    } else if (!hide && el.dataset.hgHidden) {
      el.style.removeProperty('display');
      delete el.dataset.hgHidden;
    }
    const bottom = googleBottom();
    if (el.style.getPropertyValue('bottom') !== bottom) el.style.setProperty('bottom', bottom, 'important');
    if (el.style.getPropertyValue('left') !== GOOGLE_LEFT) el.style.setProperty('left', GOOGLE_LEFT, 'important');
  }

  function watchGoogleWidget() {
    let widget = null;
    const attach = (el) => {
      if (el.dataset.hgPlaced) return;
      el.dataset.hgPlaced = '1';
      widget = el;
      placeGoogleWidget(el);
      new MutationObserver(() => placeGoogleWidget(el)).observe(el, { attributes: true, attributeFilter: ['style'], childList: true });
    };
    const reposition = () => { if (widget) placeGoogleWidget(widget); };
    window.addEventListener('resize', reposition);
    phoneQuery.addEventListener('change', reposition);

    // Both widgets load late: attach to Google's wrapper when it appears and re-measure when Smile's does.
    const existing = document.getElementById('google-merchantwidget-iframe-wrapper');
    if (existing) attach(existing);
    const obs = new MutationObserver(() => {
      const el = document.getElementById('google-merchantwidget-iframe-wrapper');
      if (el && !widget) attach(el);
      reposition();
    });
    obs.observe(document.body, { childList: true });
    window.setTimeout(() => obs.disconnect(), 20000);
  }

  /* ------------------------------------------------------------------
   * Header search bar (snippets/hg-header-search.liquid)
   * Category dropdown -> product type filters on submit; live suggestions
   * from Shopify's predictive search API (sold-out products last).
   * ------------------------------------------------------------------ */
  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function initSearch() {
    const form = document.querySelector('[data-hg-search]');
    if (!form) return;
    const input = form.querySelector('[data-hg-search-input]');
    const type = form.querySelector('[data-hg-search-type]');
    const box = form.querySelector('[data-hg-search-suggest]');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let timer = null;
    let controller = null;

    form.addEventListener('submit', () => {
      form.querySelectorAll('input[data-hg-type-filter]').forEach((el) => el.remove());
      (type.value || '').split('|').filter(Boolean).forEach((t) => {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'filter.p.product_type';
        hidden.value = t;
        hidden.setAttribute('data-hg-type-filter', '');
        form.appendChild(hidden);
      });
    });

    const hide = () => { box.hidden = true; box.innerHTML = ''; };

    async function suggest(q) {
      if (controller) controller.abort();
      controller = new AbortController();
      const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}` +
        '&resources[type]=product,collection&resources[limit]=6' +
        '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.sku,tag';
      try {
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        const r = (data && data.resources && data.resources.results) || {};
        const products = r.products || [];
        const collections = (r.collections || []).slice(0, 3);
        if (!products.length && !collections.length) {
          box.innerHTML = `<p class="hg-search__empty">No matches. Press Enter to search everything.</p>`;
          box.hidden = false;
          return;
        }
        box.innerHTML =
          (collections.length
            ? `<p class="hg-search__label">Collections</p>` + collections.map((c) =>
                `<a class="hg-search__coll" role="option" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a>`).join('')
            : '') +
          (products.length
            ? `<p class="hg-search__label">Products</p>` + products.map((p) =>
                `<a class="hg-search__prod" role="option" href="${escapeHtml(p.url)}">` +
                  (p.image ? `<img src="${escapeHtml(p.image + (p.image.includes('?') ? '&' : '?') + 'width=96')}" alt="" width="48" height="48" loading="lazy">` : '<span class="hg-search__noimg"></span>') +
                  `<span><b>${escapeHtml(p.title)}</b><small>${p.available ? '' : 'Sold out · '}$${escapeHtml(p.price)}</small></span></a>`).join('')
            : '') +
          `<button type="submit" class="hg-search__all">See all results for "${escapeHtml(q)}"</button>`;
        box.hidden = false;
      } catch (e) {
        if (e.name !== 'AbortError') hide();
      }
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { hide(); return; }
      timer = setTimeout(() => suggest(q), 200);
    });
    input.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) hide(); });
  }

  /* ------------------------------------------------------------------
   * Lift the Mira chat launcher while the footer's bottom row (copyright +
   * social icons) is on screen, so it never covers the last icon.
   * ------------------------------------------------------------------ */
  function liftLauncherAtFooter() {
    const row = document.querySelector('[id*="__utilities"], .shopify-section-group-footer-group:last-child');
    if (!row || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([e]) => {
      document.documentElement.classList.toggle('hg-footer-end', e.isIntersecting);
    }).observe(row);
  }

  function init() {
    syncAllConsents();
    initMira();
    liftLauncherAtFooter();
    watchGoogleWidget();
    initSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
